import { useState, useEffect, useCallback } from "react";
import { Plus, X, Edit2, Trash2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, Field, ImageUploader } from "./AdminShared";

export default function AdminProductsTab({ token }) {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", description: "", image_url: "", additional_images: [] });
  const [editId, setEditId] = useState(null);
  const [popupEnabled, setPopupEnabled] = useState(true);
  const load = useCallback(() => { api(token).get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api(token).get("/shop-settings").then((r) => setPopupEnabled(r.data.popup_enabled !== false)).catch(() => {});
  }, [token]);
  const togglePopup = async () => {
    const next = !popupEnabled;
    setPopupEnabled(next);
    try {
      await api(token).put("/shop-settings", { popup_enabled: next });
      toast.success(next ? "Popup ads enabled" : "Popup ads disabled");
    } catch { toast.error("Failed"); setPopupEnabled(!next); }
  };
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editId) { await api(token).put(`/products/${editId}`, payload); } else { await api(token).post("/products", payload); }
      toast.success(editId ? "Updated" : "Added");
      setForm({ name: "", price: "", description: "", image_url: "", additional_images: [] }); setEditId(null); setShowForm(false); load();
    } catch { toast.error("Failed"); }
  };
  const removeAdditionalImage = (idx) => {
    setForm((f) => ({ ...f, additional_images: f.additional_images.filter((_, i) => i !== idx) }));
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-4">
        <p className="text-zinc-500 text-sm">{products.length} product{products.length !== 1 ? "s" : ""} {products.length === 0 && "— Shop is hidden on website"}</p>
        <div className="flex items-center gap-3">
          <button onClick={togglePopup} className={`flex items-center gap-2 text-[10px] font-heading uppercase tracking-wider px-3 py-1.5 rounded-lg transition-all ${popupEnabled ? "bg-green-500/10 text-green-400" : "bg-zinc-800 text-zinc-500"}`} data-testid="popup-toggle">
            <Megaphone size={12} />
            Popup {popupEnabled ? "ON" : "OFF"}
            <div className={`w-7 h-4 rounded-full relative transition-colors ${popupEnabled ? "bg-green-500" : "bg-zinc-700"}`}>
              <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${popupEnabled ? "left-3.5" : "left-0.5"}`} />
            </div>
          </button>
          <Button onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ name: "", price: "", description: "", image_url: "", additional_images: [] }); }} className="bg-gg-red hover:bg-gg-red-hover rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="add-product-button">
          {showForm ? <><X size={14} className="mr-1" /> Cancel</> : <><Plus size={14} className="mr-1" /> Add Product</>}
        </Button>
        </div>
      </div>
      {showForm && (
        <form onSubmit={submit} className="border border-white/10 rounded-xl p-5 bg-gg-surface space-y-3">
          <Field label="Product Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Price (Rs.)" value={form.price} onChange={(v) => setForm({ ...form, price: v })} type="number" />
          <ImageUploader token={token} currentUrl={form.image_url} onUploaded={(url) => setForm({ ...form, image_url: url })} label="Main Product Image" aspect={1} />
          <div>
            <label className="text-zinc-400 text-xs font-heading uppercase tracking-wider block mb-2">Additional Images</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.additional_images.map((url, idx) => (
                <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/10 group">
                  <img src={url} alt={`Extra ${idx + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeAdditionalImage(idx)} className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <X size={14} className="text-red-400" />
                  </button>
                </div>
              ))}
            </div>
            <ImageUploader token={token} currentUrl="" onUploaded={(url) => setForm((f) => ({ ...f, additional_images: [...f.additional_images, url] }))} label="Add Image" aspect={1} />
          </div>
          <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={3} />
          <Button type="submit" className="bg-gg-red hover:bg-gg-red-hover rounded-lg font-heading uppercase tracking-wider text-xs">{editId ? "Update" : "Add Product"}</Button>
        </form>
      )}
      {products.map((p) => (
        <div key={p.id} className="border border-white/10 rounded-xl p-4 bg-gg-surface flex gap-4">
          {p.image_url && <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between">
              <div>
                <p className="text-white font-semibold text-sm">{p.name}</p>
                <p className="text-gg-red text-sm">Rs.{p.price}</p>
                {p.additional_images?.length > 0 && <p className="text-zinc-600 text-[10px]">{p.additional_images.length + 1} images</p>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setForm({ name: p.name, price: String(p.price), description: p.description, image_url: p.image_url || "", additional_images: p.additional_images || [] }); setEditId(p.id); setShowForm(true); }} className="text-zinc-500 hover:text-white"><Edit2 size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={async () => { await api(token).delete(`/products/${p.id}`); load(); }} className="text-zinc-500 hover:text-red-500"><Trash2 size={14} /></Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
